// React hook for analytics user identification
import { useCallback, useEffect } from 'react'
import { 
  identifyUser, 
  updateUserProperties, 
  trackApprovalStatusChange,
  trackPropertyInteraction,
  handleUserLogin,
  resetUserIdentification,
  debugAnalytics,
  UserProperties 
} from '../lib/analytics/user-identification'

export interface UseAnalyticsIdentificationReturn {
  // Core identification functions
  identifyUser: (user: UserProperties, consentGiven?: boolean) => void
  updateUserProperties: (updates: Partial<UserProperties>) => void
  handleLogin: (user: UserProperties, consentGiven?: boolean) => void
  handleLogout: () => void
  
  // Event tracking functions
  trackApprovalChange: (userId: string, oldStatus: string, newStatus: string) => void
  trackPropertyView: (userId: string, propertyId: string, additionalData?: Record<string, any>) => void
  trackPropertyFavorite: (userId: string, propertyId: string, additionalData?: Record<string, any>) => void
  trackOfferCreated: (userId: string, propertyId: string, additionalData?: Record<string, any>) => void
  
  // Debug function
  debug: () => void
}

export const useAnalyticsIdentification = (): UseAnalyticsIdentificationReturn => {
  // Core identification functions
  const identify = useCallback((user: UserProperties, consentGiven: boolean = true) => {
    identifyUser(user, consentGiven)
  }, [])

  const updateProperties = useCallback((updates: Partial<UserProperties>) => {
    updateUserProperties(updates)
  }, [])

  const handleLogin = useCallback((user: UserProperties, consentGiven: boolean = true) => {
    handleUserLogin(user, consentGiven)
  }, [])

  const handleLogout = useCallback(() => {
    resetUserIdentification()
  }, [])

  // Event tracking functions
  const trackApprovalChange = useCallback((userId: string, oldStatus: string, newStatus: string) => {
    trackApprovalStatusChange(userId, oldStatus, newStatus)
  }, [])

  const trackPropertyView = useCallback((userId: string, propertyId: string, additionalData?: Record<string, any>) => {
    trackPropertyInteraction(userId, propertyId, 'viewed', additionalData)
  }, [])

  const trackPropertyFavorite = useCallback((userId: string, propertyId: string, additionalData?: Record<string, any>) => {
    trackPropertyInteraction(userId, propertyId, 'favorited', additionalData)
  }, [])

  const trackOfferCreated = useCallback((userId: string, propertyId: string, additionalData?: Record<string, any>) => {
    trackPropertyInteraction(userId, propertyId, 'offer_created', additionalData)
  }, [])

  const debug = useCallback(() => {
    debugAnalytics()
  }, [])

  return {
    identifyUser: identify,
    updateUserProperties: updateProperties,
    handleLogin,
    handleLogout,
    trackApprovalChange,
    trackPropertyView,
    trackPropertyFavorite,
    trackOfferCreated,
    debug
  }
}

// Specialized hook for approval flow tracking
export const useApprovalTracking = (userId: string) => {
  const { trackApprovalChange, updateUserProperties } = useAnalyticsIdentification()

  const trackStepCompletion = useCallback((step: string, timeSpent?: number) => {
    updateUserProperties({
      [`${step}_completed_at`]: new Date().toISOString(),
      ...(timeSpent && { [`${step}_time_spent`]: timeSpent })
    })
  }, [updateUserProperties])

  const trackApprovalSubmission = useCallback((documents: string[]) => {
    updateUserProperties({
      approval_submitted_at: new Date().toISOString(),
      documents_submitted: documents.length,
      submitted_documents: documents.join(',')
    })
  }, [updateUserProperties])

  const trackApprovalDecision = useCallback((oldStatus: string, newStatus: 'approved' | 'rejected', reason?: string) => {
    trackApprovalChange(userId, oldStatus, newStatus)
    updateUserProperties({
      approval_decision_at: new Date().toISOString(),
      ...(reason && { approval_decision_reason: reason })
    })
  }, [userId, trackApprovalChange, updateUserProperties])

  return {
    trackStepCompletion,
    trackApprovalSubmission,
    trackApprovalDecision
  }
}

// Specialized hook for property interaction tracking
export const usePropertyTracking = (userId: string) => {
  const { trackPropertyView, trackPropertyFavorite, trackOfferCreated } = useAnalyticsIdentification()

  const trackView = useCallback((propertyId: string, additionalData?: {
    source?: 'search' | 'map' | 'favorites' | 'direct'
    timeOnPage?: number
    scrollDepth?: number
  }) => {
    trackPropertyView(userId, propertyId, additionalData)
  }, [userId, trackPropertyView])

  const trackFavorite = useCallback((propertyId: string, action: 'added' | 'removed') => {
    trackPropertyFavorite(userId, propertyId, { favorite_action: action })
  }, [userId, trackPropertyFavorite])

  const trackOffer = useCallback((propertyId: string, offerData: {
    offerAmount: number
    propertyPrice: number
    offerType?: string
  }) => {
    trackOfferCreated(userId, propertyId, offerData)
  }, [userId, trackOfferCreated])

  return {
    trackView,
    trackFavorite,
    trackOffer
  }
}

// Specialized hook for search and filter tracking
export const useSearchTracking = (userId: string) => {
  const { updateUserProperties } = useAnalyticsIdentification()

  const trackSearch = useCallback((searchTerm: string, resultsCount: number) => {
    if (!userId) return
    updateUserProperties({
      last_search_query: searchTerm,
      last_search_results: resultsCount,
      last_search_time: new Date().toISOString()
    })
  }, [updateUserProperties, userId])

  const trackFilterChange = useCallback((filters: {
    priceMin?: number | null
    priceMax?: number | null
    bedsMin?: number
    bathsMin?: number
    favorites?: boolean | null
    sortBy?: string
  }, resultsCount: number) => {
    if (!userId) return
    updateUserProperties({
      last_filter_price_min: filters.priceMin,
      last_filter_price_max: filters.priceMax,
      last_filter_beds_min: filters.bedsMin,
      last_filter_baths_min: filters.bathsMin,
      last_filter_favorites: filters.favorites,
      last_filter_sort: filters.sortBy,
      last_filter_results: resultsCount,
      last_filter_time: new Date().toISOString()
    })
  }, [updateUserProperties, userId])

  const trackMarketSelection = useCallback((market: string, resultsCount: number) => {
    if (!userId) return
    updateUserProperties({
      selected_market: market,
      market_results_count: resultsCount,
      market_selection_time: new Date().toISOString()
    })
  }, [updateUserProperties, userId])

  return {
    trackSearch,
    trackFilterChange,
    trackMarketSelection
  }
}

// Specialized hook for calculator tracking
export const useCalculatorTracking = (userId: string) => {
  const { updateUserProperties } = useAnalyticsIdentification()

  const trackCalculatorUsage = useCallback((data: {
    purchasePrice: number
    interestRate: number
    productType: 'saver' | 'boost'
    /** Null when `selectPayment` declined to quote; the property is then
     * omitted rather than written as a figure nobody was shown. */
    monthlyPayment: number | null
    valueShare: number
    totalValue: number
    propertyId?: string
  }) => {
    if (!userId) return
    updateUserProperties({
      last_calculator_use: new Date().toISOString(),
      last_purchase_price: data.purchasePrice,
      last_interest_rate: data.interestRate,
      last_product_selection: data.productType,
      ...(data.monthlyPayment !== null && {
        last_monthly_payment: data.monthlyPayment
      }),
      last_value_share: data.valueShare,
      last_total_value: data.totalValue,
      ...(data.propertyId && { last_calculator_property: data.propertyId })
    })
  }, [updateUserProperties, userId])

  const trackSliderInteraction = useCallback((sliderType: 'value_share' | 'monthly_payment', value: number, propertyId?: string) => {
    if (!userId) return
    updateUserProperties({
      [`${sliderType}_last_interaction`]: new Date().toISOString(),
      [`${sliderType}_last_value`]: value,
      ...(propertyId && { [`${sliderType}_last_property`]: propertyId })
    })
  }, [updateUserProperties, userId])

  const trackProductComparison = useCallback((data: {
    /** Null when `selectPayment` declined to quote — see trackCalculatorUsage. */
    saverPayment: number | null
    boostPayment: number | null
    saverValue: number
    boostValue: number
    selectedProduct: 'saver' | 'boost'
    propertyId?: string
  }) => {
    if (!userId) return
    updateUserProperties({
      product_comparison_at: new Date().toISOString(),
      ...(data.saverPayment !== null && {
        saver_monthly_payment: data.saverPayment
      }),
      ...(data.boostPayment !== null && {
        boost_monthly_payment: data.boostPayment
      }),
      saver_total_value: data.saverValue,
      boost_total_value: data.boostValue,
      selected_product: data.selectedProduct,
      ...(data.propertyId && { comparison_property: data.propertyId })
    })
  }, [updateUserProperties, userId])

  return {
    trackCalculatorUsage,
    trackSliderInteraction,
    trackProductComparison
  }
}

// Specialized hook for offer flow tracking
export const useOfferTracking = (userId: string) => {
  const { updateUserProperties } = useAnalyticsIdentification()

  const trackOfferStart = useCallback((propertyId: string, propertyData: {
    price: number
    bedrooms: number
    bathrooms: number
    location: string
  }) => {
    if (!userId) return
    updateUserProperties({
      offer_started_at: new Date().toISOString(),
      offer_property_id: propertyId,
      offer_property_price: propertyData.price,
      offer_property_bedrooms: propertyData.bedrooms,
      offer_property_bathrooms: propertyData.bathrooms,
      offer_property_location: propertyData.location
    })
  }, [updateUserProperties, userId])

  const trackOfferStep = useCallback((step: string, stepData?: Record<string, any>) => {
    if (!userId) return
    updateUserProperties({
      [`offer_step_${step}_completed`]: new Date().toISOString(),
      current_offer_step: step,
      ...stepData
    })
  }, [updateUserProperties, userId])

  const trackOfferSubmission = useCallback((offerData: {
    propertyId: string
    offerAmount: number
    productType: 'saver' | 'boost'
    monthlyPayment: number
    valueShare: number
  }) => {
    if (!userId) return
    updateUserProperties({
      offer_submitted_at: new Date().toISOString(),
      offer_amount: offerData.offerAmount,
      offer_product_type: offerData.productType,
      offer_monthly_payment: offerData.monthlyPayment,
      offer_value_share: offerData.valueShare,
      offers_submitted_total: 1 // This would be incremented for multiple offers
    })
  }, [updateUserProperties, userId])

  const trackOfferOutcome = useCallback((outcome: 'won' | 'lost' | 'closed', reason?: string) => {
    if (!userId) return
    updateUserProperties({
      last_offer_outcome: outcome,
      last_offer_outcome_at: new Date().toISOString(),
      ...(reason && { last_offer_outcome_reason: reason })
    })
  }, [updateUserProperties, userId])

  return {
    trackOfferStart,
    trackOfferStep,
    trackOfferSubmission,
    trackOfferOutcome
  }
}

// Specialized hook for user segmentation tracking
export const useUserSegmentation = (userId: string) => {
  const { updateUserProperties } = useAnalyticsIdentification()

  const trackEngagementLevel = useCallback((level: 'low' | 'medium' | 'high', metrics: {
    sessionsThisWeek: number
    propertiesViewedThisWeek: number
    calculatorUsesThisWeek: number
    favoritesCount: number
  }) => {
    if (!userId) return
    updateUserProperties({
      engagement_level: level,
      engagement_calculated_at: new Date().toISOString(),
      weekly_sessions: metrics.sessionsThisWeek,
      weekly_property_views: metrics.propertiesViewedThisWeek,
      weekly_calculator_uses: metrics.calculatorUsesThisWeek,
      total_favorites: metrics.favoritesCount
    })
  }, [updateUserProperties, userId])

  const trackBuyerIntent = useCallback((intent: 'browser' | 'serious' | 'ready', indicators: {
    hasUsedCalculator: boolean
    hasCreatedOffer: boolean
    avgTimeOnListings: number
    uniquePropertiesViewed: number
  }) => {
    if (!userId) return
    updateUserProperties({
      buyer_intent_level: intent,
      buyer_intent_updated_at: new Date().toISOString(),
      has_used_calculator: indicators.hasUsedCalculator,
      has_created_offer: indicators.hasCreatedOffer,
      avg_time_on_listings: indicators.avgTimeOnListings,
      unique_properties_viewed: indicators.uniquePropertiesViewed
    })
  }, [updateUserProperties, userId])

  const trackPreferences = useCallback((preferences: {
    preferredPriceRange?: { min: number; max: number }
    preferredBedrooms?: number
    preferredBathrooms?: number
    preferredMarkets?: string[]
    preferredProduct?: 'saver' | 'boost'
  }) => {
    if (!userId) return
    updateUserProperties({
      pref_price_min: preferences.preferredPriceRange?.min,
      pref_price_max: preferences.preferredPriceRange?.max,
      pref_bedrooms: preferences.preferredBedrooms,
      pref_bathrooms: preferences.preferredBathrooms,
      pref_markets: preferences.preferredMarkets?.join(','),
      pref_product: preferences.preferredProduct,
      preferences_updated_at: new Date().toISOString()
    })
  }, [updateUserProperties, userId])

  return {
    trackEngagementLevel,
    trackBuyerIntent,
    trackPreferences
  }
}

// Hook for automatic identification on auth state changes
export const useAutoIdentification = (user: UserProperties | null, consentGiven: boolean = true) => {
  const { handleLogin, handleLogout, updateUserProperties } = useAnalyticsIdentification()

  useEffect(() => {
    if (user?.userId) {
      // User is logged in - identify them
      handleLogin(user, consentGiven)
    } else {
      // User is logged out - reset identification
      handleLogout()
    }
  }, [user?.userId, consentGiven, handleLogin, handleLogout])

  // Auto-update properties when user data changes
  useEffect(() => {
    if (user?.userId) {
      updateUserProperties(user)
    }
  }, [user, updateUserProperties])
}